// İSG Portal — belge türü kaydı hook'u (web). TanStack Query tek önbellektir; anahtar firma
// kimliği TAŞIMAZ (platform verisi, GL7 §4.7.6). Dil değişimi yeniden getirmez, yalnız
// etiketleri yeniden çözer.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type DocumentEntity, listPlatformDocumentTypes } from '../api/documentTypes';
import type { ValidationError } from '../api/envelope';
import { isApiError } from '../api/errors';
import {
  documentTypeLabel,
  findDocumentType,
  isInactiveOrUnknownDocumentType,
  selectableTypes,
} from './documentTypeRegistry';

export const documentTypesKey = ['document-types'] as const;

const FIVE_MINUTES = 5 * 60 * 1000;

function registryQuery() {
  return {
    queryKey: documentTypesKey,
    queryFn: () => listPlatformDocumentTypes(),
    staleTime: FIVE_MINUTES,
    retry: 1,
  };
}

export function useDocumentTypes() {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const query = useQuery(registryQuery());
  const types = query.data;
  return {
    types,
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
    label: (entity: DocumentEntity, code: string) => documentTypeLabel(types, entity, code, lang),
    selectable: (entity: DocumentEntity) => selectableTypes(types, entity),
    find: (entity: DocumentEntity, code: string) => findDocumentType(types, entity, code),
    /** Kayıt yüklü ve kod kayıtta yok ya da pasif — karşılanamaz gereksinim (§27.0(6)). */
    inactiveOrUnknown: (entity: DocumentEntity, code: string) =>
      isInactiveOrUnknownDocumentType(types, entity, code),
  };
}

/**
 * Bir yükleme akışına girerken (D9) kaydı bir kez tazeler. Önbellek varken başarısız bir
 * tazeleme sessiz kalır (çağıran yalnız `isError && !types` iken hata gösterir).
 */
export function useRefetchDocumentTypesOnMount(): void {
  const { refetch } = useQuery({ ...registryQuery(), enabled: false });
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  useEffect(() => {
    void refetchRef.current();
  }, []);
}

/** Tekil tür alanları (§27.9): yükleme `type`, routing `code`, ek belge `documentType`. */
const TYPE_FIELD = /^(type|code|documentType)$/;
/** §22 gereksinim yolu (§27.0(9)): `requiredDocuments[n].code|entity`, `n` sıfır tabanlı. */
const REQUIRED_DOCUMENT_FIELD = /^requiredDocuments\[(\d+)\]\.(code|entity)$/;

/** Bu hata girdisi bir tür alanı reddi mi (`INVALID`/`INACTIVE`, §27.6). */
function isTypeFieldError(e: ValidationError): boolean {
  return (
    (e.code === 'INVALID' || e.code === 'INACTIVE') &&
    e.field !== null &&
    (TYPE_FIELD.test(e.field) || REQUIRED_DOCUMENT_FIELD.test(e.field))
  );
}

/**
 * `requiredDocuments[n]` üzerindeki tür reddinin `n`'i — GÖNDERİLEN diziye işaret eder; başka her
 * girdi için `null`.
 */
export function requiredDocumentRejectionIndex(e: ValidationError): number | null {
  const match =
    isTypeFieldError(e) && e.field !== null ? REQUIRED_DOCUMENT_FIELD.exec(e.field) : null;
  return match ? Number(match[1]) : null;
}

/** Sunucu bir tür alanını reddetti mi (INVALID|INACTIVE) — çağıran kaydı tazeler, seçimi temizler. */
export function isTypeFieldRejection(err: unknown): boolean {
  return isApiError(err) && err.errors.some(isTypeFieldError);
}
